import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Calendar16 from '@carbon/icons-react/es/calendar/16';
import { Space } from 'antd';
import moment from 'moment';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountStatus, WarningErrorCode, WarningType } from 'api/account';
import { SupportedPlansType } from 'api/billing/types';
import { PermissionSetType } from 'api/models';
import { Alert } from 'components/atoms/alert';
import { Caption01, Text02 } from 'components/styles';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useAccount } from 'hooks/use-account';
import { useIsAccountInEnterpriseBillingType } from 'hooks/use-enterprise';
import { useAccountDataQuery } from 'queries/account';
const AnnouncementBanner = styled(Alert)`
  padding: var(--spacing-03) var(--spacing-06);
  .ant-alert-message {
    width: auto;
    text-align: center;
    ${Caption01};
  }
`;
const Container = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: var(--zindex-modal);
  display: flex;
  justify-content: center;

  ${AnnouncementBanner} {
    width: 100%;
  }
`;
const BuyLink = styled.span`
  margin-left: var(--spacing-02);
  text-decoration: underline;
  cursor: pointer;
`;
const Main = styled.div``;
const DemoLink = styled.a`
  display: flex;
  align-items: center;
  ${Text02};
  > svg {
    margin-right: var(--spacing-02);
  }
`;
export const TrialExpiryBanner = ({ onBuy, onBookDemo })=>{
    var _accountData_billingMeta_subscription_plan, _accountData_billingMeta_subscription, _accountData_billingMeta, _accountData_billingMeta_subscription_plan1, _accountData_billingMeta_subscription1, _accountData_billingMeta1, _accountData_billingMeta_subscription_plan2, _accountData_billingMeta_subscription2, _accountData_billingMeta2;
    const { expiryDate, status } = useAccount();
    const currentUserPermissionSetType = useUserPermissionSetType();
    const { data: accountData } = useAccountDataQuery();
    const isAccountInEnterpriseBillingType = useIsAccountInEnterpriseBillingType();
    const planType = (accountData === null || accountData === void 0 ? void 0 : (_accountData_billingMeta = accountData.billingMeta) === null || _accountData_billingMeta === void 0 ? void 0 : (_accountData_billingMeta_subscription = _accountData_billingMeta.subscription) === null || _accountData_billingMeta_subscription === void 0 ? void 0 : (_accountData_billingMeta_subscription_plan = _accountData_billingMeta_subscription.plan) === null || _accountData_billingMeta_subscription_plan === void 0 ? void 0 : _accountData_billingMeta_subscription_plan.planType) === SupportedPlansType.Premium.toUpperCase() || (accountData === null || accountData === void 0 ? void 0 : (_accountData_billingMeta1 = accountData.billingMeta) === null || _accountData_billingMeta1 === void 0 ? void 0 : (_accountData_billingMeta_subscription1 = _accountData_billingMeta1.subscription) === null || _accountData_billingMeta_subscription1 === void 0 ? void 0 : (_accountData_billingMeta_subscription_plan1 = _accountData_billingMeta_subscription1.plan) === null || _accountData_billingMeta_subscription_plan1 === void 0 ? void 0 : _accountData_billingMeta_subscription_plan1.planType) === SupportedPlansType['Premium-V2'].toUpperCase() ? SupportedPlansType.Premium : isAccountInEnterpriseBillingType ? SupportedPlansType.Enterprise : accountData === null || accountData === void 0 ? void 0 : (_accountData_billingMeta2 = accountData.billingMeta) === null || _accountData_billingMeta2 === void 0 ? void 0 : (_accountData_billingMeta_subscription2 = _accountData_billingMeta2.subscription) === null || _accountData_billingMeta_subscription2 === void 0 ? void 0 : (_accountData_billingMeta_subscription_plan2 = _accountData_billingMeta_subscription2.plan) === null || _accountData_billingMeta_subscription_plan2 === void 0 ? void 0 : _accountData_billingMeta_subscription_plan2.planType.toUpperCase();
    const { expiringIn, isExpiringSoon, isAlreadyExpired } = useMemo(()=>{
        const today = moment().endOf('day');
        const expiry = moment(expiryDate).endOf('day');
        const result = today.diff(expiry, 'days');
        const _isAlreadyExpired = result > 0;
        return {
            expiringIn: _isAlreadyExpired ? 0 : Math.abs(result),
            isAlreadyExpired: _isAlreadyExpired,
            isExpiringSoon: Math.abs(result) <= 7
        };
    }, [
        expiryDate
    ]);
    if (status !== AccountStatus.Trial) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Container, {
        id: "account-expiry-banner",
        children: /*#__PURE__*/ _jsx(AnnouncementBanner, {
            type: isExpiringSoon ? 'warning' : 'info',
            closable: false,
            message: /*#__PURE__*/ _jsxs(Space, {
                size: 96,
                children: [
                    /*#__PURE__*/ _jsxs(Main, {
                        children: [
                            isAlreadyExpired ? /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "account.trial.expired.message",
                                values: {
                                    planType
                                }
                            }) : /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "account.trial.expiry.message",
                                values: {
                                    days: expiringIn,
                                    planType
                                }
                            }),
                            !(isAlreadyExpired && currentUserPermissionSetType === PermissionSetType.AccountAdmin) ? /*#__PURE__*/ _jsx(BuyLink, {
                                onClick: onBuy,
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account.trial.buy"
                                })
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(DemoLink, {
                        onClick: onBookDemo,
                        target: "_blank",
                        children: [
                            /*#__PURE__*/ _jsx(Calendar16, {}),
                            /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "account.trial.book_demo"
                            })
                        ]
                    })
                ]
            })
        })
    });
};
export const SubscriptionRenewalFailedBanner = ()=>{
    const { accountWarnings } = useAccount();
    const hasWarning = useMemo(()=>accountWarnings === null || accountWarnings === void 0 ? void 0 : accountWarnings.find(({ warningType, errorCode })=>warningType === WarningType.Billing && errorCode === WarningErrorCode.PaymentFailed), [
        accountWarnings
    ]);
    if (!hasWarning) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    return /*#__PURE__*/ _jsx(Container, {
        id: "account-expiry-banner",
        children: /*#__PURE__*/ _jsx(AnnouncementBanner, {
            type: "error",
            closable: false,
            message: /*#__PURE__*/ _jsx(Space, {
                size: 96,
                children: /*#__PURE__*/ _jsx(Main, {
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "billing.subscription.renewal.failed.banner.message",
                        values: {
                            b: (chunks)=>/*#__PURE__*/ _jsx("b", {
                                    children: chunks
                                })
                        }
                    })
                })
            })
        })
    });
};
